import { Container, Badge } from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import confirmAlert from '../../../helpers/confirmAlert';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import CommonHeader from '../../../components/Headers/CommonHeader';
import ClassService from '../../../services/Class';
import ClassInterface, { validStatus, statusEnum } from '../../../interfaces/ClassInterface';
import dateFormat from '../../../helpers/dateFormat';
import { FilterInterface, FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import { PagedMetaInterface, createPagedMeta } from '../../../entities/PagedMeta';
import { OptionsPagedInterface, createOptionsPage } from '../../../entities/OptionsPaged';
import DataTable from '../../../components/Core/DataTable';
import isAdmin from '../../../helpers/isAdmin';
import isDirector from '../../../helpers/isDirector';
import ClassTypeModal from './ClassTypeModal';
import isEducationDirectorAdminAssistant from '../../../helpers/isEducationDirectorAdminAssistant';
import isUpperManagement from '../../../helpers/isUpperManagement';

const ClassList = () => {
  const { token, currentSchool, user } = useContext(AuthContext);
  const hasEditPermission =
    isAdmin(user) || isDirector(user) || isEducationDirectorAdminAssistant(user) || isUpperManagement(user);
  const isAdminUser = isAdmin(user);
  const hasDeletePermission = isAdmin(user) || isUpperManagement(user);

  const columns = [
    {
      name: 'Name',
      route: '/class',
      label: (row: any) => row.name,
      disabled: !hasEditPermission,
    },
    {
      name: 'Franchisee',
      selector: (row: any) => row.school?.franchisee,
      grow: 1,
    },
    {
      name: 'School',
      selector: (row: any) => row.school?.name,
      grow: 1,
    },
    {
      name: 'Status',
      selector: (row: any) => row.status,
      grow: 1,
      cell: (row: any) => (
        <Badge
          color=""
          className="badge-dot badge-lg"
          style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}
        >
          <i
            className={`bg-${validStatus?.[(row?.status || '') as statusEnum]?.color} mr-2`}
            style={{ verticalAlign: 'baseline' }}
          />
          {validStatus?.[(row?.status || '') as statusEnum]?.value}
        </Badge>
      ),
    },
    {
      name: 'Created at',
      selector: (row: any) => row.createdAt,
      cell: (row: any) => dateFormat(row.createdAt),
      grow: 2,
    },
    {
      name: 'Updated at',
      selector: (row: any) => row.updatedAt,
      cell: (row: any) => dateFormat(row.updatedAt),
      grow: 2,
    },
    hasDeletePermission && {
      type: 'delete',
      onClick: (row: any) => {
        deleteItemAlert(row.id);
      },
    },
  ];

  const [data, setData] = useState([] as any[]);
  const [loading, setLoading] = useState(false);
  const [classTypeModal, setClassTypeModal] = useState(false);
  const [pagedMeta, setPagedMeta] = useState(createPagedMeta());
  const [filteredOptions, setFilteredOptions] = useState({
    filters: [],
    options: createOptionsPage(pagedMeta),
  } as { filters: FilterInterface[]; options: OptionsPagedInterface });

  useEffect(() => {
    loadList(filteredOptions);
  }, [filteredOptions]);

  useEffect(() => {
    setFilteredOptions({
      filters: [],
      options: createOptionsPage(pagedMeta),
    });
  }, [currentSchool]);

  const loadList = async (filteredOptions: FilteredOptionsInterface) => {
    try {
      setLoading(true);

      const filter = {
        field: 'schoolId',
        operation: 'hash',
        value: currentSchool.value,
      };
      filteredOptions.filters?.push(filter);

      const result = await ClassService(token || '').list(filteredOptions);
      const data: ClassInterface[] = result.data;
      setData(data);
      const meta: PagedMetaInterface = result.meta;
      if (meta) {
        setPagedMeta(meta);
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error fetching list');
      return toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (text: string) => {
    const filterFirstName = {
      field: 'name',
      operation: 'ilike',
      value: text || '',
    };
    const newFilteredOptions = {
      filters: [filterFirstName],
      options: filteredOptions.options,
    };
    newFilteredOptions.options.page = 1;
    setFilteredOptions(newFilteredOptions);
  };

  const deleteItemAlert = async (id: string) => {
    return confirmAlert({ title: 'Delete', messageType: 'DELETE', id, onClickYes: deleteItem });
  };

  const deleteItem = async (id: string) => {
    try {
      setLoading(true);
      await ClassService(token || '').delete(id);
      toast.success(`Deleted successfully!`);
      loadList(filteredOptions);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || `Error deleting item: ${id}`);
      return toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePerRowsChange = async (perPage: number) => {
    const newOptions = {
      ...pagedMeta,
      perPage,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions),
    };
    setFilteredOptions(newFilteredOptions);
  };

  const handlePageChange = async (page: number) => {
    const newOptions = {
      ...pagedMeta,
      currentPage: page,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions),
    };
    setFilteredOptions(newFilteredOptions);
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <DataTable
          title="Class List"
          columns={columns}
          data={data}
          totalRows={pagedMeta.total}
          loading={loading}
          button={hasEditPermission}
          buttonUrl="/class/new"
          settingsButton={isAdminUser}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          onClickSettings={() => setClassTypeModal(!classTypeModal)}
          search={true}
          onSearch={handleSearch}
        />
      </Container>
      <ClassTypeModal isOpen={classTypeModal} toggleModal={() => setClassTypeModal(!classTypeModal)} />
    </>
  );
};

export default ClassList;
